/**
 * A project file on the canvas (RFC 0277 S6, S8, S7c).
 *
 * The content comes from the git server, never from a document: `workingFile`
 * for a worktree, `fileAt` for a ref. A file in a worktree is EDITABLE, like
 * VS Code with Auto Save on: the app edits a local draft, and each write goes
 * back through `/save` carrying the hash of what was read, so a file an agent
 * changed underneath is refused rather than overwritten (fileDraft.ts). A file
 * read at a commit stays a read-only snapshot: there is nowhere to save it.
 *
 * A file goes to WHICHEVER APP HANDLES ITS MIME, falling back to the one for
 * `text/plain`: the same dispatch a document goes through, so a `.md` in the
 * repo opens in the editor a markdown document does, and a `.ts` in the Code
 * app.
 * No app installed means a plain `<pre>`, which is what a `text/plain`
 * document falls to as well.
 *
 * NOT a CodeMirror of its own. The host had one for a week, with fourteen
 * grammar packages compiled into mod-app; the Code app in `.mod/packages`
 * carries them now, like the text editor carries its own.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo'
import {
  type ComponentType,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { App, type AppFileDiff, snapshotHandle } from '../../sdk/host/App'
import { useContribution } from '../../sdk/host/useContribution'
import { fetchVcsFromServer } from '../../sdk/vcs'
import {
  codeFileId,
  parseCodeId,
  worktreeBranch,
} from '../../services/code/codeFile'
import {
  type Autosave,
  type AutosaveState,
  createAutosave,
  draftHandle,
  type SaveAnswer,
} from '../../services/code/fileDraft'
import { lineDiff } from '../../services/code/lineDiff'
import { useDocument } from '../../services/world/repo/useDocument'
import { useInstalledApps } from '../../services/world/useInstalledApps'
import { useWorldHandlers } from '../../services/world/useWorldHandlers'
import { ContributionWaiting } from '../../shell/slots/ContributionWaiting'
import { handlerFor } from '../../shell/slots/renderers'
import type { Subject } from '../../shell/window/subject'
import type { ChangedList } from './codeFiles'
import { comparisonForFile, type FileDiffLine, fileDiff } from './fileDiff'

type Loaded =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; text: string | null }

export type ReadCodeFile = (
  path: string,
  ref: string | null,
  projectId: string,
  sourceId?: string,
) => Promise<string | null>

/** How a code id's ref becomes a git server read. Exported for its test. */
export const readCodeFile: ReadCodeFile = async (
  path,
  ref,
  projectId,
  sourceId,
) => {
  // A `~` ref is a branch's working tree in its own worktree (RFC 0329 3b):
  // what an agent wrote there, committed or not.
  const branch = worktreeBranch(ref)
  if (branch)
    return (await fetchVcsFromServer(
      'workingFile',
      [path, branch],
      projectId,
      sourceId,
    )) as string | null
  return (
    ref
      ? await fetchVcsFromServer('fileAt', [ref, path], projectId, sourceId)
      : await fetchVcsFromServer('workingFile', [path], projectId, sourceId)
  ) as string | null
}

export type SaveCodeFile = (
  req: { path: string; content: string; readAt: string; branch?: string },
  projectId: string,
  sourceId?: string,
) => Promise<SaveAnswer>

export type TrackedCodeFile = (
  path: string,
  branch: string | null,
  projectId: string,
  sourceId?: string,
) => Promise<boolean>

/** Whether git holds a previous version, for the warning before a first save. */
export const trackedCodeFile: TrackedCodeFile = async (
  path,
  branch,
  projectId,
  sourceId,
) =>
  (await fetchVcsFromServer('tracked', [path, branch], projectId, sourceId)) ===
  true

/** The git server's guarded write (RFC 0277 3e). Exported for its test. */
export const saveCodeFile: SaveCodeFile = async (req, projectId, sourceId) =>
  (await fetchVcsFromServer('save', [req], projectId, sourceId)) as SaveAnswer

/** The window's comment layer, handed in by the shell: a feature does not
 *  import another feature's components (RFC 0314 3e). */
export type CommentLayer = ComponentType<{
  projectId: string
  documentId: string
  className?: string
  disabled?: boolean
  children: ReactNode
}>

const NoComments: CommentLayer = ({ children }) => (
  <div className="relative min-h-0 flex-1 overflow-y-auto">{children}</div>
)

export function CodeFileView({
  file,
  projectId,
  Comments = NoComments,
  read = readCodeFile,
  save = saveCodeFile,
  tracked = trackedCodeFile,
  diffMode = false,
  onOpenCodeFile,
}: {
  file: Subject
  projectId: string
  /** Where comments on this file are made and shown (RFC 0277 S13). */
  Comments?: CommentLayer
  read?: ReadCodeFile
  save?: SaveCodeFile
  tracked?: TrackedCodeFile
  /** The Files column's Changed preference (RFC 0354 3f). */
  diffMode?: boolean
  onOpenCodeFile?: (id: string) => void
}) {
  // Bumped by "Discard mine": read the file again and start a fresh draft.
  const [reload, setReload] = useState(0)
  const target = parseCodeId(file.id)
  const path = target?.path ?? ''
  const ref = target?.ref ?? null
  const sourceId = file.id.startsWith('code-source:')
    ? target?.sourceId
    : undefined
  const [loaded, setLoaded] = useState<{ id: string; value: Loaded }>({
    id: file.id,
    value: { status: 'loading' },
  })

  // `reload` is a trigger ("Discard mine"), not a value the read uses.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload re-reads
  useEffect(() => {
    let live = true
    setLoaded({ id: file.id, value: { status: 'loading' } })
    read(path, ref, projectId, sourceId).then(
      (text) => {
        if (live) setLoaded({ id: file.id, value: { status: 'ready', text } })
      },
      (e: unknown) => {
        if (live)
          setLoaded({
            id: file.id,
            value: {
              status: 'error',
              message: e instanceof Error ? e.message : String(e),
            },
          })
      },
    )
    return () => {
      live = false
    }
  }, [file.id, path, ref, read, projectId, reload])

  // Derived against the id, so a pane switched to another file never shows the
  // previous file's bytes for the render before the effect fires.
  const state: Loaded =
    loaded.id === file.id ? loaded.value : { status: 'loading' }

  const text = state.status === 'ready' ? state.text : null
  const [comparison, setComparison] = useState<{
    id: string
    status: 'loading' | 'ready' | 'unavailable'
    from?: string
    before?: string
    lines?: FileDiffLine[]
  }>({ id: file.id, status: 'loading' })
  useEffect(() => {
    if (!diffMode || state.status !== 'ready') return
    let live = true
    setComparison({ id: file.id, status: 'loading' })
    void (async () => {
      try {
        const list = (await fetchVcsFromServer(
          'changed',
          [ref ?? ''],
          projectId,
          sourceId,
        )) as ChangedList
        const result = await comparisonForFile(
          list,
          path,
          state.text,
          (oldPath, from) => read(oldPath, from, projectId, sourceId),
        )
        if (live)
          setComparison(
            result
              ? { id: file.id, status: 'ready', ...result }
              : { id: file.id, status: 'unavailable' },
          )
      } catch {
        if (live) setComparison({ id: file.id, status: 'unavailable' })
      }
    })()
    return () => {
      live = false
    }
  }, [diffMode, file.id, path, ref, projectId, sourceId, read, state])
  const activeComparison = comparison.id === file.id ? comparison : null
  const diffSource = useMemo(() => createFileDiffSource(), [])
  const before =
    diffMode && activeComparison?.status === 'ready'
      ? (activeComparison.before ?? null)
      : null
  useEffect(() => diffSource.set(before), [diffSource, before])
  const branch = worktreeBranch(ref)
  // A deleted file has no bytes at its selected ref. Show an empty snapshot so
  // the editor can draw its removed lines as widgets, without offering a save.
  const displayText =
    text === null && activeComparison?.status === 'ready' ? '' : text
  // A worktree can be written; a commit cannot (RFC 0277 3f). A file that is
  // not on disk has nothing to edit.
  const editable = text !== null && (ref === null || !!branch)
  const snapshot = useMemo(
    () =>
      displayText !== null && !editable
        ? snapshotHandle(file.id, {
            path,
            mime: file.mime,
            content: displayText,
          })
        : null,
    [file.id, file.mime, path, displayText, editable],
  )
  const draft = useMemo(
    () =>
      editable && text !== null
        ? draftHandle(file.id, { path, mime: file.mime, content: text })
        : null,
    [file.id, file.mime, path, text, editable],
  )

  const [saveState, setSaveState] = useState<AutosaveState>({
    kind: 'saved',
    tracked: null,
  })
  const [showTheirs, setShowTheirs] = useState(false)
  const autosave = useRef<Autosave | null>(null)
  useEffect(() => {
    if (!draft) return
    setSaveState({ kind: 'saved', tracked: null })
    setShowTheirs(false)
    const auto = createAutosave({
      text: () => draft.text(),
      read: text,
      write: (content, readAt) =>
        save(
          { path, content, readAt, ...(branch ? { branch } : {}) },
          projectId,
          sourceId,
        ),
      onState: setSaveState,
    })
    autosave.current = auto
    // Before the first save: an untracked file's first write cannot be undone,
    // and the header says so while there is still time to decide.
    let live = true
    tracked(path, branch, projectId, sourceId).then(
      (isTracked) => {
        if (!live) return
        setSaveState((s) =>
          s.kind === 'saved' && s.tracked === null
            ? { kind: 'saved', tracked: isTracked }
            : s,
        )
      },
      () => {},
    )
    const onChange = () => auto.edited()
    draft.on('change', onChange)
    return () => {
      live = false
      draft.off('change', onChange)
      auto.dispose()
      if (autosave.current === auto) autosave.current = null
    }
  }, [draft, text, path, branch, projectId, sourceId, save, tracked])

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onKeyDownCapture={(e) => {
        // ⌘S writes at once, the same as VS Code, and never reaches the page.
        if (!draft || e.key !== 's' || !(e.metaKey || e.ctrlKey)) return
        e.preventDefault()
        void autosave.current?.save()
      }}
    >
      <div className="flex shrink-0 items-center gap-3 border-border/60 border-b px-4 py-1 text-[11px] text-muted-foreground">
        <span>
          {/* Said only of what can never be saved (a commit), not of a file
              still loading: a worktree read can take a moment. */}
          {ref !== null && !branch ? 'Read-only, ' : ''}
          {branch
            ? `working tree of ${branch}`
            : ref
              ? `at ${ref}`
              : 'working tree'}
        </span>
        {activeComparison?.status === 'ready' && diffMode ? (
          <span>Changes from {activeComparison.from}</span>
        ) : null}
        {draft ? (
          <SaveStatus
            state={saveState}
            showingTheirs={showTheirs}
            onShowChanges={() => setShowTheirs((v) => !v)}
            onDiscard={() => setReload((n) => n + 1)}
          />
        ) : null}
      </div>
      {draft && saveState.kind === 'refused' && showTheirs ? (
        <ChangesPanel mine={draft.text()} theirs={saveState.theirs} />
      ) : null}
      {snapshot ? (
        // Outside the scrolling wrapper below: a host-realm app lays itself out
        // and owns its own scrolling, the same as it does under a document.
        <CodeApp
          file={file}
          projectId={projectId}
          Comments={Comments}
          snapshot={snapshot}
          text={displayText ?? ''}
          fileDiff={diffSource}
        />
      ) : draft ? (
        <CodeApp
          file={file}
          projectId={projectId}
          Comments={Comments}
          snapshot={draft}
          text={text ?? ''}
          fileDiff={diffSource}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          {state.status === 'loading' ? (
            <p className="p-6 text-muted-foreground/60 text-sm">
              Reading {path}…
            </p>
          ) : state.status === 'error' ? (
            <p className="p-6 text-muted-foreground text-sm">{state.message}</p>
          ) : state.text === null ? (
            <div className="p-6 text-muted-foreground text-sm">
              <p>
                {ref
                  ? `${path} does not exist at ${ref} in this source, or is binary.`
                  : `${path} is not on disk in this source, or is binary.`}
              </p>
              {sourceId && sourceId !== 'default' && onOpenCodeFile && (
                <button
                  type="button"
                  onClick={() => onOpenCodeFile(codeFileId(path, ref))}
                  className="mt-2 text-foreground underline underline-offset-2"
                >
                  Try in Project files
                </button>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

/**
 * The app that handles `text/plain`, mounted on the snapshot, or a `<pre>`.
 *
 * The same three steps `DocumentView` takes for a document: ask the catalog
 * who handles the mime, fetch that app's code, mount it in the host realm.
 * `absent` draws the fallback; `loading` says so rather than showing one view
 * and then another.
 */
function CodeApp({
  file,
  projectId,
  Comments,
  snapshot,
  text,
  fileDiff,
}: {
  file: Subject
  projectId: string
  Comments: CommentLayer
  snapshot: ReturnType<typeof snapshotHandle>
  text: string
  fileDiff: AppFileDiff
}) {
  const [ws] = useDocument(projectId as AnyDocumentId)
  const installed = useInstalledApps(ws)
  const worldHandlers = useWorldHandlers()
  const app = useMemo(
    () =>
      (file.mime && handlerFor(installed, file.mime, worldHandlers)) ||
      handlerFor(installed, 'text/plain', worldHandlers),
    [installed, worldHandlers, file.mime],
  )
  const view = useContribution(
    app?.realm === 'host' ? app.viewDocId : undefined,
  )
  // An app that comments itself (the text editor) takes the passage from its
  // own selection; until it claims that, the window's selection popover does.
  const [appComments, setAppComments] = useState(false)

  if (app?.realm === 'host' && view.state === 'loading') {
    return (
      <ContributionWaiting
        name={app.name}
        what="the code view's"
        recovery="Settings (⌘,) then Open with brings the plain view back."
      />
    )
  }
  if (app?.realm === 'host' && view.state === 'ready') {
    return (
      // A comment on a file is a comment on its code id (RFC 0277 S13): the
      // path, at the branch or worktree it was read from, anchored by the
      // passage it quotes, so it lands in the same catalog as a document's.
      <Comments
        projectId={projectId}
        documentId={file.id}
        className="relative flex min-h-0 flex-1 flex-col"
        disabled={appComments}
      >
        <App
          // Remount when the app is rebuilt or the file changes, for the same
          // reason a document's editor does: a mounted app read its snapshot
          // once and will not notice either on its own.
          key={`${app.id}:${view.code.length}:${file.id}:${text.length}`}
          code={view.code}
          documentId={file.id}
          projectId={projectId}
          title={app.name}
          snapshot={snapshot}
          fileDiff={fileDiff}
          onClaimComments={() => setAppComments(true)}
        />
      </Comments>
    )
  }
  return (
    <Comments projectId={projectId} documentId={file.id}>
      <pre className="whitespace-pre-wrap px-6 py-8 font-mono text-foreground text-sm">
        {text}
      </pre>
    </Comments>
  )
}

function createFileDiffSource(): AppFileDiff & {
  set: (before: string | null) => void
} {
  let before: string | null = null
  const listeners = new Set<() => void>()
  return {
    lines: (currentText) =>
      before === null ? null : fileDiff(before, currentText),
    subscribe: (fn) => {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    set: (next) => {
      if (next === before) return
      before = next
      for (const listener of listeners) listener()
    },
  }
}

/**
 * Where an edited file stands (RFC 0277 3f): the words in the header, and the
 * two moves a refusal offers. There is no Overwrite: someone who wants theirs
 * to win copies it, discards, and saves again, which is the step that makes
 * them look at what the agent wrote.
 */
function SaveStatus({
  state,
  showingTheirs,
  onShowChanges,
  onDiscard,
}: {
  state: AutosaveState
  showingTheirs: boolean
  onShowChanges: () => void
  onDiscard: () => void
}) {
  if (state.kind === 'refused') {
    return (
      <span className="flex items-center gap-2 text-foreground">
        Changed on disk, not saved.
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={onShowChanges}
        >
          {showingTheirs ? 'Hide changes' : 'Show changes'}
        </button>
        <button
          type="button"
          className="underline underline-offset-2"
          onClick={onDiscard}
        >
          Discard mine
        </button>
      </span>
    )
  }
  if (state.kind === 'failed')
    return <span className="text-foreground">Not saved: {state.message}</span>
  if (state.kind === 'edited') return <span>Edited</span>
  if (state.kind === 'saving') return <span>Saving…</span>
  if (state.tracked === false)
    return <span>Not in git, a save cannot be undone</span>
  return <span>Saved</span>
}

/**
 * "Show changes": yours against what is on disk now, line by line. A file too
 * large to diff here, or one deleted or binary on disk, shows what it can.
 */
function ChangesPanel({
  mine,
  theirs,
}: {
  mine: string
  theirs: string | null
}) {
  const lines = theirs === null ? null : lineDiff(mine, theirs)
  return (
    <div className="max-h-64 shrink-0 overflow-auto border-border/60 border-b bg-muted/30 py-2 font-mono text-xs">
      <p className="px-4 pb-1 font-sans text-[11px] text-muted-foreground">
        − yours, + on disk now
      </p>
      {theirs === null ? (
        <p className="px-4 text-muted-foreground">
          Deleted on disk, or binary.
        </p>
      ) : lines === null ? (
        <pre className="whitespace-pre-wrap px-4 text-foreground">{theirs}</pre>
      ) : (
        lines.map((l, i) => (
          <div
            // A diff's lines have no identity beyond their position.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional
            key={i}
            className={
              l.kind === 'mine'
                ? 'whitespace-pre-wrap px-4 text-red-400'
                : l.kind === 'theirs'
                  ? 'whitespace-pre-wrap px-4 text-green-400'
                  : 'whitespace-pre-wrap px-4 text-muted-foreground'
            }
          >
            {l.kind === 'mine' ? '− ' : l.kind === 'theirs' ? '+ ' : '  '}
            {l.text}
          </div>
        ))
      )}
    </div>
  )
}
